// ช่องวันที่ dd/mm/yyyy และเวลา 24 ชม. (HH:mm) — ไม่ขึ้นกับภาษาของเบราว์เซอร์
// ค่าภายในยังเป็น "YYYY-MM-DD" / "HH:mm" เหมือนเดิม (ส่ง API ได้ตรง ๆ)
import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { CalendarDays, Clock } from 'lucide-react';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> & {
  value?: string | number | readonly string[];
  onChange?: (e: { target: { value: string } }) => void;
};

const cls = 'w-full rounded-xl border-2 border-line bg-white py-3 pl-4 pr-14 text-lg placeholder:text-muted/60 focus:border-brand focus:outline-none disabled:bg-st-slate-soft';

export function isoToDmy(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

export function dmyToIso(dmy: string): string | null {
  const m = dmy.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  let y = Number(m[3]);
  if (y > 2400) y -= 543; // พิมพ์ปี พ.ศ. มาก็รับได้
  const mo = Number(m[2]);
  const d = Number(m[1]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** ใส่ / ให้อัตโนมัติขณะพิมพ์: 19102026 → 19/10/2026 */
function maskDate(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

function openPicker(el: HTMLInputElement | null) {
  if (!el) return;
  try {
    el.showPicker();
  } catch {
    el.focus();
    el.click();
  }
}

export function DateInput({ value, onChange, className, min, max, disabled, ...rest }: Props) {
  const iso = typeof value === 'string' ? value : '';
  const [text, setText] = useState(isoToDmy(iso));
  const native = useRef<HTMLInputElement>(null);
  useEffect(() => setText(isoToDmy(iso)), [iso]);

  const commit = (t: string) => {
    if (!t) return onChange?.({ target: { value: '' } });
    const v = dmyToIso(t);
    if (v) onChange?.({ target: { value: v } });
  };

  return (
    <div className="relative">
      <input
        {...rest}
        type="text"
        inputMode="numeric"
        placeholder="วว/ดด/ปปปป"
        autoComplete="off"
        disabled={disabled}
        value={text}
        className={`${cls} ${className ?? ''}`}
        onChange={(e) => {
          const t = maskDate(e.target.value);
          setText(t);
          if (t.length === 10 || t === '') commit(t);
        }}
        onBlur={(e) => {
          if (text && !dmyToIso(text)) setText(isoToDmy(iso));
          rest.onBlur?.(e);
        }}
      />
      <button type="button" disabled={disabled} onClick={() => openPicker(native.current)} className="absolute right-1.5 top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-lg text-muted hover:bg-brand-soft hover:text-brand" aria-label="เลือกจากปฏิทิน">
        <CalendarDays className="size-6" />
      </button>
      <input
        ref={native}
        type="date"
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none absolute bottom-0 right-0 h-0 w-0 opacity-0"
        value={iso}
        min={min}
        max={max}
        onChange={(e) => onChange?.({ target: { value: e.target.value } })}
      />
    </div>
  );
}

function maskTime(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 4);
  return d.length <= 2 ? d : `${d.slice(0, 2)}:${d.slice(2)}`;
}

function validTime(t: string): boolean {
  const m = t.match(/^(\d{2}):(\d{2})$/);
  return !!m && Number(m[1]) <= 23 && Number(m[2]) <= 59;
}

export function TimeInput({ value, onChange, className, disabled, ...rest }: Props) {
  const v = typeof value === 'string' ? value : '';
  const [text, setText] = useState(v);
  const native = useRef<HTMLInputElement>(null);
  useEffect(() => setText(v), [v]);
  return (
    <div className="relative">
      <input
        {...rest}
        type="text"
        inputMode="numeric"
        placeholder="ชช:นน"
        autoComplete="off"
        disabled={disabled}
        value={text}
        className={`${cls} ${className ?? ''}`}
        onChange={(e) => {
          const t = maskTime(e.target.value);
          setText(t);
          if (validTime(t)) onChange?.({ target: { value: t } });
          if (t === '') onChange?.({ target: { value: '' } });
        }}
        onBlur={(e) => {
          if (text && !validTime(text)) setText(v);
          rest.onBlur?.(e);
        }}
      />
      <button type="button" disabled={disabled} onClick={() => openPicker(native.current)} className="absolute right-1.5 top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-lg text-muted hover:bg-brand-soft hover:text-brand" aria-label="เลือกเวลา">
        <Clock className="size-6" />
      </button>
      <input
        ref={native}
        type="time"
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none absolute bottom-0 right-0 h-0 w-0 opacity-0"
        value={v}
        onChange={(e) => onChange?.({ target: { value: e.target.value } })}
      />
    </div>
  );
}
